import { createSvgEl } from './svg';
import type { Vec2 } from '@learnforge/demo-math/geometry';

export interface GridOptions {
  parent: SVGElement;
  width: number;
  height: number;
  unit: number;
  faint?: boolean;
}

export interface GridHandle {
  worldToScreen(p: Vec2): Vec2;
  screenToWorld(p: Vec2): Vec2;
  fillRegion(x0: number, y0: number, x1: number, y1: number): void;
  clearFill(): void;
  /** Show or hide the lattice/axes (coordinate maths keep working when hidden). */
  setVisible(visible: boolean): void;
  destroy(): void;
}

/**
 * Creates a lattice grid with axes and optional unit-square fill regions.
 *
 * **Origin convention:** The world origin is placed at (0, 0) in world coordinates,
 * which maps to screen position (0, 0). Gridlines are drawn at integer coordinates
 * starting from 1 (skipping the origin itself). Axes are drawn separately at x=0 and y=0.
 *
 * For a grid with width and height in pixels and unit spacing in pixels per world unit:
 * - World x-range: [0, width/unit]
 * - World y-range: [0, height/unit]
 *
 * Example: width=800, height=460, unit=40 produces:
 * - World range: [0, 20] × [0, 11.5]
 * - Gridlines at integer x: 1, 2, ..., 19 (19 vertical gridlines)
 * - Gridlines at integer y: 1, 2, ..., 11 (11 horizontal gridlines)
 * - Axes at x=0 and y=0 (2 axis lines)
 *
 * Colors are applied via CSS classes:
 * - `.geo-gridline` for lattice gridlines
 * - `.geo-axis` for the x and y axes
 * - `.geo-fill-rect` for unit-square fill regions
 * - `.geo-fill-label` for the count label
 * - `.geo-grid--faint` class on the parent group for faint rendering
 */
export function createGrid(options: GridOptions): GridHandle {
  const { parent, width, height, unit, faint = false } = options;

  // Origin at (0, 0) in world coordinates
  const originWorldX = 0;
  const originWorldY = 0;

  // Screen position of the world origin
  const originScreenX = originWorldX * unit;
  const originScreenY = originWorldY * unit;

  // Conversion functions
  const worldToScreen = (p: Vec2): Vec2 => ({
    x: p.x * unit + originScreenX,
    y: p.y * unit + originScreenY,
  });

  const screenToWorld = (p: Vec2): Vec2 => ({
    x: (p.x - originScreenX) / unit,
    y: (p.y - originScreenY) / unit,
  });

  // Create main grid group
  const gridGroup = createSvgEl('g', {
    class: faint ? 'geo-grid geo-grid--faint' : 'geo-grid',
  });
  parent.appendChild(gridGroup);

  // Container for gridlines (z-order: drawn first, behind everything)
  const gridlineGroup = createSvgEl('g', { 'data-role': 'gridlines' });
  gridGroup.appendChild(gridlineGroup);

  // Calculate world bounds (rounded to the nearest integer)
  const worldMaxX = width / unit;
  const worldMaxY = height / unit;

  // Draw gridlines at interior integer coordinates (skip the origin at 0)
  // Use a small epsilon to exclude exact boundaries (when worldMax is a whole number)
  const minIntX = 1;
  const maxIntX = Math.floor(worldMaxX - 1e-9);
  const minIntY = 1;
  const maxIntY = Math.floor(worldMaxY - 1e-9);

  // Draw vertical gridlines (at integer x coordinates)
  for (let x = minIntX; x <= maxIntX; x++) {
    const screenX = worldToScreen({ x, y: 0 }).x;
    const line = createSvgEl('line', {
      x1: screenX,
      y1: 0,
      x2: screenX,
      y2: height,
      class: 'geo-gridline',
    });
    gridlineGroup.appendChild(line);
  }

  // Draw horizontal gridlines (at integer y coordinates)
  for (let y = minIntY; y <= maxIntY; y++) {
    const screenY = worldToScreen({ x: 0, y }).y;
    const line = createSvgEl('line', {
      x1: 0,
      y1: screenY,
      x2: width,
      y2: screenY,
      class: 'geo-gridline',
    });
    gridlineGroup.appendChild(line);
  }

  // Draw x-axis (at y = 0)
  const xAxis = createSvgEl('line', {
    x1: 0,
    y1: originScreenY,
    x2: width,
    y2: originScreenY,
    class: 'geo-axis',
  });
  gridlineGroup.appendChild(xAxis);

  // Draw y-axis (at x = 0)
  const yAxis = createSvgEl('line', {
    x1: originScreenX,
    y1: 0,
    x2: originScreenX,
    y2: height,
    class: 'geo-axis',
  });
  gridlineGroup.appendChild(yAxis);

  // Container for fill regions (z-order: drawn after gridlines)
  const fillGroup = createSvgEl('g', { 'data-role': 'fills' });
  gridGroup.appendChild(fillGroup);

  // Handle fill regions
  const fillRegion = (x0: number, y0: number, x1: number, y1: number): void => {
    clearFill();

    const nx = Math.abs(x1 - x0);
    const ny = Math.abs(y1 - y0);

    const minX = Math.min(x0, x1);
    const minY = Math.min(y0, y1);

    // Draw unit-square fill rectangles
    for (let i = 0; i < nx; i++) {
      for (let j = 0; j < ny; j++) {
        const rectX0 = minX + i;
        const rectY0 = minY + j;
        const rectX1 = rectX0 + 1;
        const rectY1 = rectY0 + 1;

        const topLeft = worldToScreen({ x: rectX0, y: rectY0 });
        const bottomRight = worldToScreen({ x: rectX1, y: rectY1 });

        const rectWidth = bottomRight.x - topLeft.x;
        const rectHeight = bottomRight.y - topLeft.y;

        const rect = createSvgEl('rect', {
          x: topLeft.x,
          y: topLeft.y,
          width: rectWidth,
          height: rectHeight,
          'data-role': 'fill-rect',
          class: 'geo-fill-rect',
        });
        fillGroup.appendChild(rect);
      }
    }

    // Draw count label at the center of the region
    const count = nx * ny;
    const centerWorldX = (minX + minX + nx) / 2;
    const centerWorldY = (minY + minY + ny) / 2;
    const centerScreen = worldToScreen({ x: centerWorldX, y: centerWorldY });

    const label = createSvgEl('text', {
      x: centerScreen.x,
      y: centerScreen.y,
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
      'data-role': 'fill-label',
      class: 'geo-fill-label',
    });
    label.textContent = count.toString();
    fillGroup.appendChild(label);
  };

  // Handle clear fill
  const clearFill = (): void => {
    fillGroup.innerHTML = '';
  };

  // Toggle lattice/axes visibility without affecting coordinate maths.
  const setVisible = (visible: boolean): void => {
    gridGroup.style.display = visible ? '' : 'none';
  };

  // Handle destroy
  const destroy = (): void => {
    if (gridGroup.parentNode) {
      gridGroup.parentNode.removeChild(gridGroup);
    }
  };

  return {
    worldToScreen,
    screenToWorld,
    fillRegion,
    clearFill,
    setVisible,
    destroy,
  };
}
